// 1. Imports
import "./SwipeableModals.scss";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { FormControl, InputLabel, OutlinedInput } from "@mui/material";
import { useState, useEffect } from "react";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import BlueButton from "../../../components/BlueButton/BlueButton";
import api from "../../../utils/api";
import dayjs from "dayjs";
import "dayjs/locale/ar"; // Import Arabic locale for dayjs
import { toast } from "react-toastify";

// 2. Main Component
const SwipeableModals = ({
  open,
  onClose,
  onOpen,
  currentView,
  selectedCode,
  refreshData,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    code: "",
    discount: "",
    maxDiscount: "", // Added maxDiscount field
    maxUsage: "",
    expireAt: null,
  });

  const [loading, setLoading] = useState(false);

  // Reset form when modal opens/closes or when selectedCode changes
  useEffect(() => {
    if (open && currentView === "editDiscountCode" && selectedCode) {
      // Format dates for edit
      const expiry = selectedCode.expireAt
        ? dayjs(selectedCode.expireAt)
        : null;

      setFormData({
        code: selectedCode.code || "",
        discount: selectedCode.discount || "",
        maxDiscount: selectedCode.maxDiscount || "", // Added maxDiscount field
        maxUsage: selectedCode.maxUsage || "",
        expireAt: expiry,
      });
    } else if (open && currentView === "createDiscountCode") {
      // Reset form for create
      setFormData({
        code: "",
        discount: "",
        maxDiscount: "", // Added maxDiscount field
        maxUsage: "",
        expireAt: null,
      });
    }
  }, [open, currentView, selectedCode]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    // Extract field name from id
    const fieldName = id.replace("outlined-adornment-", "");

    // For discount, maxDiscount and maxUsage, prevent negative values
    if (
      (fieldName === "discount" ||
        fieldName === "maxUsage" ||
        fieldName === "maxDiscount") &&
      value.startsWith("-")
    ) {
      return; // Don't update state if value is negative
    }

    setFormData({
      ...formData,
      [fieldName]: value,
    });
  };

  // Handle date changes
  const handleDateChange = (field, date) => {
    setFormData({
      ...formData,
      [field]: date,
    });
  };

  // Validate form
  const validateForm = () => {
    if (!formData.code || formData.code.trim() === "") {
      toast.error("يجب إدخال رمز كود الخصم");
      return false;
    }

    if (!formData.discount || isNaN(Number(formData.discount))) {
      toast.error("يجب إدخال قيمة صحيحة للخصم");
      return false;
    }

    if (Number(formData.discount) < 0) {
      toast.error("قيمة الخصم يجب أن تكون أكبر من أو تساوي صفر");
      return false;
    }

    // Validate maxDiscount
    if (formData.maxDiscount && isNaN(Number(formData.maxDiscount))) {
      toast.error("يجب إدخال قيمة صحيحة للحد الأقصى للخصم");
      return false;
    }

    if (formData.maxDiscount && Number(formData.maxDiscount) < 0) {
      toast.error("الحد الأقصى للخصم يجب أن يكون أكبر من أو يساوي صفر");
      return false;
    }

    if (
      !formData.maxUsage ||
      isNaN(Number(formData.maxUsage)) ||
      Number(formData.maxUsage) <= 0
    ) {
      toast.error("يجب إدخال عدد صحيح موجب لمرات الاستخدام");
      return false;
    }

    if (!formData.expireAt) {
      toast.error("يجب تحديد تاريخ الانتهاء");
      return false;
    }

    if (formData.expireAt.isBefore(dayjs().startOf("day"))) {
      toast.error("تاريخ الانتهاء لا يمكن أن يكون في الماضي");
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        code: formData.code,
        discount: Number(formData.discount),
        maxDiscount: formData.maxDiscount ? Number(formData.maxDiscount) : null, // Added maxDiscount field
        maxUsage: Number(formData.maxUsage),
        expireAt: formData.expireAt.format("YYYY-MM-DD"),
      };

      const { code, ...dataToEdit } = payload;

      let response;

      if (currentView === "createDiscountCode") {
        // Create new discount code
        response = await api.post("/promoCodes", payload);
        toast.success("تم انشاء الكود بنجاح");
      } else if (currentView === "editDiscountCode" && selectedCode) {
        // Update existing discount code
        response = await api.patch(
          `/promoCodes/${selectedCode._id}`,
          dataToEdit
        );
        toast.success("تم تعديل الكود بنجاح");
      }

      if (response.data.status === "success") {
        // Close modal and refresh data
        onClose();
        if (refreshData) {
          refreshData();
        }
      }
    } catch (error) {
      console.error("Error saving discount code:", error);
      // Handle API error messages
      if (error.response && error.response.data && error.response.data.errors) {
        const apiErrors = error.response.data.errors;
        Object.keys(apiErrors).forEach((key) => {
          toast.error(apiErrors[key]);
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Render create/edit discount code view (same for both)
  const renderDiscountCodeView = () => (
    <div className="user-modal">
      <p className="user-modal-p">
        {currentView === "createDiscountCode"
          ? "اضافة كود خصم"
          : "تعديل كود الخصم"}
      </p>
      <form onSubmit={handleSubmit}>
        {currentView === "createDiscountCode" && (
          <FormControl
            sx={{ width: "100%", mb: 2 }}
            variant="outlined"
            required
          >
            <InputLabel
              htmlFor="outlined-adornment-code"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              رمز كود الخصم
            </InputLabel>
            <OutlinedInput
              sx={{ borderRadius: "8px" }}
              id="outlined-adornment-code"
              type="text"
              notched
              value={formData.code}
              onChange={handleInputChange}
            />
          </FormControl>
        )}

        <FormControl sx={{ width: "100%", mb: 2 }} variant="outlined" required>
          <InputLabel
            htmlFor="outlined-adornment-discount"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            نسبة الخصم
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            id="outlined-adornment-discount"
            type="text"
            notched
            placeholder="%"
            value={formData.discount}
            onChange={handleInputChange}
            inputProps={{
              min: 0,
              pattern: "[0-9]*\\.?[0-9]*",
            }}
          />
        </FormControl>

        {/* New maxDiscount FormControl */}
        <FormControl sx={{ width: "100%", mb: 2 }} variant="outlined" required>
          <InputLabel
            htmlFor="outlined-adornment-maxDiscount"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            الحد الأقصى للخصم
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            id="outlined-adornment-maxDiscount"
            type="text"
            placeholder="ريال"
            notched
            value={formData.maxDiscount}
            onChange={handleInputChange}
            inputProps={{
              min: 0,
              pattern: "[0-9]*\\.?[0-9]*",
            }}
          />
        </FormControl>

        <FormControl sx={{ width: "100%", mb: 2 }} variant="outlined" required>
          <InputLabel
            htmlFor="outlined-adornment-maxUsage"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            عدد مرات الاستخدام
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: "8px" }}
            id="outlined-adornment-maxUsage"
            type="text"
            notched
            value={formData.maxUsage}
            onChange={handleInputChange}
            inputProps={{
              min: 1,
              pattern: "[0-9]*",
            }}
          />
        </FormControl>

        <FormControl sx={{ width: "100%", mb: 2 }} variant="outlined" required>
          <InputLabel
            htmlFor="outlined-adornment-expireAt"
            shrink
            sx={{
              background: "white",
              transformOrigin: "right top",
              transform: "translate(14px, -9px) scale(0.75)",
              right: 24,
              left: "auto",
              paddingLeft: "4px",
              paddingRight: "4px",
            }}
          >
            تاريخ الانتهاء
          </InputLabel>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            sx={{ width: "100%" }}
          >
            <DemoContainer
              components={["DatePicker"]}
              sx={{ width: "100%", p: 0 }}
            >
              <DatePicker
                sx={{ width: "100%" }}
                value={formData.expireAt}
                onChange={(date) => handleDateChange("expireAt", date)}
                slotProps={{
                  textField: {
                    sx: { width: "100%" },
                  },
                }}
              />
            </DemoContainer>
          </LocalizationProvider>
        </FormControl>

        <BlueButton
          text={currentView === "createDiscountCode" ? "اضافة" : "تحديث"}
          size="medium"
          variant="fill"
          type="submit"
          loading={loading}
          disabled={loading}
        />
      </form>
    </div>
  );

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      sx={{ zIndex: "1202" }}
      disableScrollLock
    >
      {renderDiscountCodeView()}
    </SwipeableDrawer>
  );
};

export default SwipeableModals;
